import React, { useState } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { useSelector } from 'react-redux';

import { Button } from '@components/ui/button';
import useGroups from '@hooks/useGroups';
import { groupsStore } from '@hooks/useGroups/groupsStore';
import ImportCsvDialog from '@modules/PartnersList/components/SavedCreators/ImportCsvDialog';
import type { RootState } from '@app-types/index';

/** What the importer accepts, said once wherever import is offered. The server
 * reads profile links from any column and falls back to spotting handles. */
export const IMPORT_HINT =
  'Export your tracking sheet as CSV. Any column with Instagram, TikTok or YouTube links or handles works.';

/** Import writes lists: view-only members can't (same gate as the saved
 * creators' own actions). */
export const useCanImportCreators = (): boolean =>
  useSelector(
    (state: RootState) => state.Managemant?.user?.permission !== 'view',
  );

/** Creators-area write actions (track, invite, import) share the view-only
 * gate; the server re-checks the specific permission on each call. */
export const useCanManageCreators = useCanImportCreators;

/** Opens the saved-creators CSV import from anywhere in the Creators area. */
export const useImportSpreadsheet = () => {
  const [open, setOpen] = useState(false);
  const { groups } = useGroups();

  const dialog = (
    <ImportCsvDialog
      open={open}
      groups={groups}
      onOpenChange={(next) => {
        setOpen(next);
        // The import creates or fills a list; refresh so it shows up.
        if (!next) void groupsStore.invalidate();
      }}
    />
  );

  return { openImport: () => setOpen(true), dialog };
};

export const ImportSpreadsheetButton: React.FC<{
  variant?: 'outline' | 'default';
  size?: 'default' | 'lg';
  className?: string;
}> = ({ variant = 'outline', size = 'default', className }) => {
  const { openImport, dialog } = useImportSpreadsheet();
  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={className ?? 'cursor-pointer'}
        title={IMPORT_HINT}
        onClick={openImport}
      >
        <FileSpreadsheet />
        Import from spreadsheet
      </Button>
      {dialog}
    </>
  );
};
